import { PropsWithChildren, ReactElement, useCallback, useMemo } from 'react'
import styles from 'components/atoms/inputs/SelectTypeAhead/SelectTypeAhead.styl'
import { useSelect } from 'components/atoms/inputs/Select/useSelect'
import { SelectHandler } from 'components/atoms/inputs/Select/SelectHandler/SelectHandler'
import { DropdownPreset } from 'components/atoms/inputs/Select/DropdownPreset/DropdownPreset'
import { Controller, useFormContext } from 'react-hook-form'
import { FutureSelectInput } from 'components/atoms/inputs/FutureSelectInput/FutureSelectInput'
import { Option, InputRenderer } from 'components/atoms/inputs/Select/parts/types'
import { TypeAheadHandler } from 'components/atoms/inputs/Select/TypeAheadHandler/TypeAheadHandler'

type SelectTypeAheadProps<Value> = {
  options: Option<Value>[]
  value: Value
  renderer?: InputRenderer
  onChange: (value: Value | null) => void
}
export const SelectTypeAhead = <Value extends any>(props: PropsWithChildren<SelectTypeAheadProps<Value>>): ReactElement | null => {
  const {
    renderer,
    options,
  } = props

  const Input = useMemo(() => renderer || FutureSelectInput, [renderer])

  const selectContext = useSelect(props)
  const { isOpen, inputRef } = selectContext

  const handleFocus = useCallback(() => {
    const { current } = inputRef
    if (!current) {
      return
    }

    current.focus()
    current.select()
  }, [inputRef])

  return (
    <SelectHandler context={ selectContext }>
      <TypeAheadHandler context={ selectContext } options={ options }>
        <div className={ styles.select }>
          <Input
            autoComplete='no'
            innerRef={ inputRef.set }
            onFocus={ handleFocus }
          />
          { isOpen.visible && (
            <DropdownPreset<Value> context={ selectContext } />
          )}
        </div>
      </TypeAheadHandler>
    </SelectHandler>
  )
}

export type SelectTypeAheadControllerProps<Value> =
  & Pick<SelectTypeAheadProps<Value>, 'options' | 'renderer'>
  & {
    name: string
  }
export const SelectTypeAheadController = <Value extends any>(props: PropsWithChildren<SelectTypeAheadControllerProps<Value>>): ReactElement | null => {
  const { name, renderer, options } = props
  const { control } = useFormContext()

  const inputRenderer = useCallback(({ field: { value, onChange }}) => (
    <SelectTypeAhead<Value>
      value={ value }
      options={ options }
      renderer={ renderer }
      onChange={ onChange }
    />
  ), [options, renderer])

  return (
    <Controller
      control={ control }
      name={ name }
      render={ inputRenderer }
    />
  )
}

